/**
 * Client for /api/reports/v2/* -- the Performance Report's own endpoints. Definitions live on the server
 * (app/services/performance_report_service.py); nothing here recomputes a number.
 */
import api from '@/utils/api';
import type { Filters } from './types';

export interface ApiWorkRow {
  agent_id: number | null; bucket: string; received: number; worked: number; worked_manual: number; worked_tata: number;
  worked_unsplit: number; closed_by_agent: number; closed_by_system: number; unique_customers: number;
  unique_orders: number | null; work_items: number; events?: number | null;
}
export interface ApiOrderRow {
  agent_id: number | null; bucket: string | null; orders: number; valid_orders: number; cancelled_orders: number; units: number;
  valid_revenue: number; cancelled_value: number; tag_orders: number; coupon_orders: number;
  /** Everything credited, split into five delivery stages that add up to it (same rule as the Agent Dashboard). */
  booked?: number;
  st_delivered_orders?: number; st_delivered_value?: number; st_in_transit_orders?: number; st_in_transit_value?: number;
  st_awaiting_pickup_orders?: number; st_awaiting_pickup_value?: number; st_returned_orders?: number; st_returned_value?: number;
  st_cancelled_orders?: number; st_cancelled_value?: number;
  /** The same split for orders credited by Agent Tag only (coupon = total minus tag). */
  tg_delivered_orders?: number; tg_delivered_value?: number; tg_in_transit_orders?: number; tg_in_transit_value?: number;
  tg_awaiting_pickup_orders?: number; tg_awaiting_pickup_value?: number; tg_returned_orders?: number; tg_returned_value?: number;
  tg_cancelled_orders?: number; tg_cancelled_value?: number;
}
export interface ApiCallRow {
  agent_id: number | null; bucket: string | null; dials: number; connected: number; hung_up_early: number; no_answer: number;
  cancelled_ringing: number; dropped_other: number; talk_seconds: number; short_calls: number; ring_seconds: number;
  customers_called: number; customers_connected: number; active_days: number;
}
export interface ApiSummary {
  success: boolean; data_as_of: string; own_only: boolean; range: { from: string; to: string };
  agents: Record<string, string>;
  work: ApiWorkRow[]; orders: ApiOrderRow[]; calls: ApiCallRow[];
  call_extras: {
    hourly: { hour: number; dials: number; connected: number }[];
    attempts: { customers: number; a1: number; a2: number; a3: number; a4plus: number; never: number };
    not_in_connectivity: { inbound: number; dialer_clicks: number; ai_calls: number };
  };
  orders_over_time: { period: string; valid: number; cancelled: number }[];
  unique_customers: number;
  /** Distinct customers with agent work per bucket, all agents together. */
  unique_customers_by_bucket?: Record<string, number>;
  coverage: Record<string, string | null>;
  definitions: { min_talk_seconds: number; short_call_seconds: number; attribution_lock_days: number; abc_call_window_days: number };
  automation?: { engati_ai: number; abc_auto_updated: number; other_system: number };
  no_agent_orders?: { orders: number; valid_orders: number; valid_revenue: number; cancelled_value: number };
}
export interface ApiCategoryRow {
  category: string; product: string; agent_id: number | null; orders: number; valid_orders: number; cancelled_orders: number;
  units: number; valid_revenue: number; cancelled_value: number;
  booked?: number;
  st_delivered_orders?: number; st_delivered_value?: number; st_in_transit_orders?: number; st_in_transit_value?: number;
  st_awaiting_pickup_orders?: number; st_awaiting_pickup_value?: number; st_returned_orders?: number; st_returned_value?: number;
  st_cancelled_orders?: number; st_cancelled_value?: number;
}
export interface ApiCategories { success: boolean; agents: Record<string, string>; rows: ApiCategoryRow[] }

export interface DrillRequest {
  kind: 'work' | 'orders' | 'calls'; bucket?: string | null; agent_id?: number | null; no_agent?: boolean; no_bucket?: boolean;
  worked?: string | null; status?: string | null; basis?: string | null; category?: string | null; product?: string | null; agents_only?: boolean;
  outcome?: string | null; hour?: number | null; page?: number; page_size?: number;
}
export interface ApiDrill { success: boolean; total: number; rows: Record<string, unknown>[] }

/** Dates and the filters the server understands: agent and bucket (everything else is applied on screen). */
export function baseBody(f: Filters, agentId: number | null) {
  return {
    from_date: f.from, to_date: f.to, from_time: f.fromTime || null, to_time: f.toTime || null, grouping: f.grouping,
    agent_id: agentId, // One bucket is filtered by the server; several are fetched whole and narrowed on screen (see stats.applyFilters).
    bucket: f.buckets.length === 1 ? f.buckets[0] : null,
  };
}

const unwrap = <T,>(res: unknown): T => res as unknown as T;

export async function postSummary(f: Filters, agentId: number | null): Promise<ApiSummary> {
  return unwrap<ApiSummary>(await api.req({ url: '/reports/v2/summary', method: api.Method.POST, data: baseBody(f, agentId) }));
}

/** Super admins only: rebuild this view from live data and replace the cached copy. */
export async function postSummaryRefresh(f: Filters, agentId: number | null): Promise<ApiSummary> {
  return unwrap<ApiSummary>(await api.req({ url: '/reports/v2/summary/refresh', method: api.Method.POST, data: baseBody(f, agentId) }));
}

export async function postCategories(f: Filters, agentId: number | null): Promise<ApiCategories> {
  const data = { ...baseBody(f, agentId), category: f.category === 'all' ? null : f.category, product: f.product === 'all' ? null : f.product };
  return unwrap<ApiCategories>(await api.req({ url: '/reports/v2/categories', method: api.Method.POST, data }));
}

export type HourlyKind = 'work' | 'orders' | 'calls';
export type WithHour<T> = T & { hour: number };
export interface ApiHourly<T> { success: boolean; kind: HourlyKind; data_as_of: string; rows: WithHour<T>[] }

/** The tab's own rows split by hour of day (IST) instead of by agent: one row per (bucket, hour), all days added together. */
export async function postHourly<T>(f: Filters, agentId: number | null, kind: HourlyKind): Promise<ApiHourly<T>> {
  return unwrap<ApiHourly<T>>(await api.req({ url: '/reports/v2/hourly', method: api.Method.POST, data: { ...baseBody(f, agentId), kind } }));
}

export async function postDrill(f: Filters, agentId: number | null, req: DrillRequest): Promise<ApiDrill> {
  return unwrap<ApiDrill>(await api.req({ url: '/reports/v2/drilldown', method: api.Method.POST, data: { ...baseBody(f, agentId), ...req } }));
}

function bearer(): string | null {
  const tokenCookie = document.cookie.split(';').find(c => c.trim().startsWith('crm_auth_token='));
  if (tokenCookie) return tokenCookie.split('=')[1];
  return import.meta.env.VITE_BEARER_TOKEN || null;
}

/** POST /export and save the CSV. The shared client always parses JSON, so this fetches directly with the same auth. */
export interface ExportExtras {
  bucket?: string | null; worked?: string | null; status?: string | null; basis?: string | null; category?: string | null; product?: string | null; agents_only?: boolean;
  outcome?: string | null; no_bucket?: boolean;
}

export async function downloadExport(dataset: string, f: Filters, agentId: number | null, noAgent = false, extra: ExportExtras = {}): Promise<number> {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://api.growtheye.com/api';
  const token = bearer();
  const res = await fetch(`${baseUrl}/reports/v2/export`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      ...baseBody(f, agentId), dataset, no_agent: noAgent,
      category: extra.category ?? (f.category === 'all' ? null : f.category), product: extra.product ?? (f.product === 'all' ? null : f.product),
      ...(extra.bucket !== undefined ? { bucket: extra.bucket } : {}), worked: extra.worked ?? null, status: extra.status ?? null,
      outcome: extra.outcome ?? null, no_bucket: extra.no_bucket ?? false, basis: extra.basis ?? null, agents_only: extra.agents_only ?? false,
    }),
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
  const rows = Number(res.headers.get('X-Row-Count') || 0);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `performance-report_${dataset}_${f.from}_${f.to}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
  return rows;
}
